import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { useAsyncData } from '@/hooks';
import { institutionTemplatesApi } from '@/api/institution-templates';
import type { InstitutionTemplateDefinition } from '@/types/institution-templates';
import styles from './platform-pages.module.css';

/**
 * The hierarchy template catalogue — every institution type's operating skeleton,
 * as identity seeds it.
 *
 * READ AND PREVIEW ONLY. These fifteen rows are migration-seeded reference data;
 * nothing on this page or its detail view writes anything, and the detail view's
 * experiment mode says so in a banner. The page exists so a platform operator can
 * answer "what does a francophone secondary school get on day one?" without
 * reading a seed file.
 *
 * Grouped by institution type rather than listed flat because the question always
 * arrives type-first: nobody asks for TPL_COMBINED_SCHOOL_BILINGUAL by name, they
 * ask what combined schools look like and then which subsystem.
 */
export function InstitutionTemplatesPage() {
  const { t } = useTranslation();

  const { state, reload } = useAsyncData<InstitutionTemplateDefinition[]>(
    'institution-templates',
    () => institutionTemplatesApi.list(),
    true,
  );

  const groups = useMemo(() => {
    if (state.status !== 'ready') return [];
    const byType = new Map<string, InstitutionTemplateDefinition[]>();
    for (const template of state.data) {
      const list = byType.get(template.institutionType) ?? [];
      list.push(template);
      byType.set(template.institutionType, list);
    }
    // The server already orders by type then subsystem; Map preserves that.
    return [...byType.entries()];
  }, [state]);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('templates.title')}</h1>
          <p className={styles.pageLead}>{t('templates.lead')}</p>
        </div>
      </header>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('templates.empty')}
        emptyBody={t('templates.emptyBody')}
      >
        {() => (
          <>
            {groups.map(([institutionType, templates]) => (
              <section key={institutionType}>
                <h2 className={styles.subheading}>
                  {t(`admin.profile.institutionTypes.${institutionType}`, {
                    defaultValue: institutionType,
                  })}
                </h2>
                <ul className={styles.rowList}>
                  {templates.map((template) => (
                    <li key={template.code} className={styles.row}>
                      <div className={styles.rowMain}>
                        <Link
                          className={styles.rowTitleLink}
                          to={`/admin/institution-templates/${encodeURIComponent(template.code)}`}
                        >
                          {template.displayName}
                        </Link>
                        <div className={styles.rowMetaLine}>
                          <code className={styles.codeTag}>{template.code}</code>
                          <span className={styles.infoTag}>
                            {t(`templates.subsystems.${template.educationSubsystem}`, {
                              defaultValue: template.educationSubsystem,
                            })}
                          </span>
                          <span
                            className={
                              template.capabilityClass === 'OPERATIONAL'
                                ? styles.okTag
                                : styles.warnTag
                            }
                          >
                            {t(`templates.capabilityClasses.${template.capabilityClass}`, {
                              defaultValue: template.capabilityClass,
                            })}
                          </span>
                          {template.needsReview && (
                            <span className={styles.warningTag}>{t('templates.needsReview')}</span>
                          )}
                        </div>
                        {template.description && (
                          <p className={styles.rowDetail}>{template.description}</p>
                        )}
                      </div>
                      <div className={styles.rowAside}>
                        <span className={styles.mutedCell}>
                          {t('templates.rungCount', { total: template.hierarchy.length })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <p className={styles.footnote}>{t('templates.readOnlyNote')}</p>
          </>
        )}
      </AsyncPanel>
    </div>
  );
}
